import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pin, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Compact homepage strip that surfaces the currently-pinned (Editor's Pick)
 * WIM Blog article. Fetches /api/blog (which sorts is_featured first) and
 * gracefully renders nothing if no pinned article exists.
 */
export default function FeaturedArticleStrip() {
  const [post, setPost] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .get("/api/blog", { signal: ctrl.signal })
      .then((r) => {
        const items = r.data.posts || [];
        const pick = items.find((p) => p.is_featured);
        if (pick) setPost(pick);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  if (!post) return null;

  return (
    <section className="section pt-0" data-testid="home-featured-article">
      <div className="container-jcc">
        <Link
          to={`/wim/${post.slug}`}
          className="group block rounded-3xl border border-purple/15 bg-gradient-to-br from-white via-purple/[0.03] to-teal/[0.04] p-6 md:p-10 shadow-[0_2px_14px_rgba(106,27,154,0.06)] hover:shadow-[0_14px_40px_rgba(106,27,154,0.10)] transition-shadow relative"
        >
          <span className="absolute -top-3 left-8 pill bg-purple text-white shadow-sm inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Editor's Pick
          </span>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
            <div>
              <div className="overline text-purple mb-3 inline-flex items-center gap-2">
                <Pin className="w-3.5 h-3.5" /> Start here
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight group-hover:text-purple transition-colors">
                {post.title}
              </h3>
              {post.excerpt && (
                <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
                <span className="pill bg-teal-50 text-teal">{post.category}</span>
                {post.read_minutes ? (
                  <span className="text-ink-soft">{post.read_minutes} min read</span>
                ) : null}
              </div>
            </div>
            <div className="md:pl-6 md:border-l md:border-slate-200">
              <span className="inline-flex items-center gap-2 font-semibold text-purple group-hover:gap-3 transition-all">
                Read the full article <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
